import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { Project, CreateProjectData } from "../../store/types";
import { getErrorMessage } from "./use-getError";

export const useProjectsAPI = () => {
  const projectsEP = useAppSelector((state) => state.endpoints.projects);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [projectsDropdownOpts, setProjectDropdownOpts] = useState<
    { label: string; value: any }[]
  >([]);
  const { toastRef, showSuccess, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const { data: fetchedProjects, refetch: refetchProjects } = useGetQuery(
    projectsEP,
    {
      refetchOnMountOrArgChange: true,
      skip: !projectsEP,
    }
  );

  const refetchData = async () => {
    await refetchProjects();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const handlePostProject = async (
    project: CreateProjectData
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: projectsEP,
        id: "",
        body: project,
      });

      if (result.data) {
        showSuccess("Успешно", "Проект создан");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("создать проект", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("создать проект", error);
      return false;
    }
  };

  const handleUpdateProject = async (project: Project): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: projectsEP,
        id: project?.id,
        body: project,
      });

      if (result.data) {
        showSuccess("Успешно", "Данные проекта обновлены");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("обновить проект", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("обновить проект", error);
      return false;
    }
  };

  const handleDeleteProject = async (project: Project): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: projectsEP,
        id: project?.id,
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError("удалить проект", serverError ?? result.error);
        return false;
      }

      showSuccess("Успешно", "Проект удален");
      return true;
    } catch (error) {
      handleError("удалить проект", error);
      return false;
    }
  };

  useEffect(() => {
    if (fetchedProjects) setProjects(fetchedProjects);
  }, [fetchedProjects]);

  useEffect(() => {
    if (projects) {
      const opt = projects.map((project: Project) => ({
        label: project.name,
        value: project.id,
      }));

      setProjectDropdownOpts(opt);
    }
  }, [projects]);

  return {
    projectsEP,
    handlePostProject,
    handleUpdateProject,
    handleDeleteProject,
    refetchData,
    projects,
    projectsDropdownOpts,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
  };
};
